import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Trash2, UserPlus, ChevronRight, TrendingUp, TrendingDown, Target, Award, BarChart2, Minus, Link2, KeyRound, Copy, Eye, BookOpen, Play, CheckCircle2, XCircle, ChevronDown, ChevronUp, Trophy, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TestReviewDialog } from "@/components/TestReviewDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VenueManagement } from "@/components/VenueManagement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [setupLink, setSetupLink] = useState(null);
  const [reviewResultId, setReviewResultId] = useState(null);
  const [expandedTest, setExpandedTest] = useState(null);
  const [testLeaderboards, setTestLeaderboards] = useState({});

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    setAdding(true);
    try {
      // Create user account (which also creates staff record)
      const response = await axios.post(`${API}/users/create`, { name: newName.trim() });
      const fullLink = `${window.location.origin}${response.data.setup_link}`;
      setSetupLink(fullLink);
      setNewName("");
      toast.success("User account created!");
      fetchStaff(); // Refresh staff list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setAdding(false);
    }
  };

  const handleResetPassword = async (member) => {
    try {
      // staff.id === users.user_id by convention (Phase 0.5 staff/user
      // linkage) — use it directly instead of an unreliable name-match
      // against GET /users, which could collide across two same-named
      // staff in different organisations.
      const res = await axios.post(`${API}/users/${member.id}/reset-password`);
      const fullLink = `${window.location.origin}${res.data.setup_link}`;
      setSetupLink(fullLink);
      toast.success("Password reset link generated");
    } catch (error) {
      toast.error("Failed to reset password");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(setupLink);
    toast.success("Link copied to clipboard!");
  };

  const handleDeleteStaff = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/staff/${deleteTarget.id}`);
      setStaff(prev => prev.filter(s => s.id !== deleteTarget.id));
      toast.success("Staff member removed");
    } catch (error) {
      toast.error("Failed to remove staff member");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openStaffDetails = async (member) => {
    setSelectedStaff(member);
    setLoadingPerformance(true);
    try {
      const res = await axios.get(`${API}/staff/${member.id}/performance`);
      setPerformance(res.data);
    } catch (error) {
      console.error("Failed to fetch performance:", error);
      setPerformance(null);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const closeStaffDetails = () => {
    setSelectedStaff(null);
    setPerformance(null);
    setExpandedTest(null);
    setTestLeaderboards({});
  };

  const toggleLeaderboard = async (testId) => {
    if (expandedTest === testId) {
      setExpandedTest(null);
      return;
    }
    setExpandedTest(testId);
    if (!testLeaderboards[testId]) {
      try {
        const res = await axios.get(`${API}/saved-tests/${testId}/leaderboard`);
        setTestLeaderboards(prev => ({ ...prev, [testId]: res.data }));
      } catch {
        setTestLeaderboards(prev => ({ ...prev, [testId]: { leaderboard: [] } }));
      }
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-[#C36A2F]";
    return "text-red-500";
  };

  const getProgressColor = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-[#C36A2F]";
    return "bg-red-500";
  };

  const stats = performance?.stats;
  const trend = performance?.trend;
  const recentResults = performance?.recent_results || [];
  const typeStats = performance?.type_stats || {};
  const assignedTests = performance?.assigned_tests || [];
  const studyDecks = performance?.study_decks || [];

  return (
    <div data-testid="staff-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Team</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">
          Manage your venues, staff and access codes
        </p>
      </div>

      {/* Venue Management — moved here from admin dashboard */}
      <VenueManagement />

      {/* Legacy free-text staff card retained for shells that don't yet run
          VenueManagement. When VenueManagement is mounted it already lists
          every user; showing this again duplicates the roster and confuses
          the flow, so both the "Add Staff Member" form and the second
          "Staff Members" list are hidden. Toggle via ?legacy=1 on the URL
          if a legacy client ever needs them back. */}
      {new URLSearchParams(window.location.search).get("legacy") === "1" && (<>
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="text-[hsl(var(--primary))]" size={24} />
            Add Staff Member
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddStaff} className="flex gap-3" data-testid="add-staff-form">
            <Input
              placeholder="Enter staff name..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] flex-1 max-w-md"
              data-testid="staff-name-input"
            />
            <Button
              type="submit"
              disabled={adding}
              className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="add-staff-btn"
            >
              <Plus size={18} className="mr-2" />
              {adding ? "Adding..." : "Add"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Staff List */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="text-[hsl(var(--primary))]" size={24} />
            Staff Members ({staff.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="staff-loading">
              Loading...
            </div>
          ) : staff.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-staff">
              <Users size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
              <p className="text-[hsl(var(--muted-foreground))]">No staff members yet</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Add your first team member above
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="staff-list">
              {staff.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                  onClick={() => openStaffDetails(member)}
                  data-testid={`staff-item-${member.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                      <span className="text-[hsl(var(--primary))] font-semibold">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        Added {new Date(member.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleResetPassword(member); }}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                      title="Reset Password"
                      data-testid={`reset-pwd-${member.id}`}
                    >
                      <KeyRound size={16} />
                    </Button>
                    <ChevronRight size={20} className="text-[hsl(var(--muted-foreground))]" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(member); }}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-staff-${member.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Staff Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {deleteTarget?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStaff}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff Performance Dialog */}
      <Dialog open={!!selectedStaff} onOpenChange={closeStaffDetails}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                <span className="text-[hsl(var(--primary))] text-xl font-bold">
                  {selectedStaff?.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <span className="text-2xl">{selectedStaff?.name}</span>
                <p className="text-sm text-[hsl(var(--muted-foreground))] font-normal">Performance Dashboard</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingPerformance ? (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="performance-loading">
              Loading performance data...
            </div>
          ) : !stats || stats.total_tests === 0 ? (
            <div className="py-8 text-center" data-testid="no-performance-data">
              <Target size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
              <p className="text-[hsl(var(--muted-foreground))]">No test results yet</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                This staff member hasn&apos;t completed any tests
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="performance-stats-grid">
                <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] text-center" data-testid="stat-avg-score">
                  <p className={`text-3xl font-bold ${getScoreColor(stats.avg_percentage)}`}>
                    {stats.avg_percentage.toFixed(0)}%
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Average Score</p>
                </div>
                <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] text-center" data-testid="stat-tests-taken">
                  <p className="text-3xl font-bold text-[hsl(var(--primary))]">{stats.total_tests}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Tests Taken</p>
                </div>
                <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] text-center col-span-2 md:col-span-1" data-testid="stat-best-score">
                  <p className="text-3xl font-bold text-green-500">
                    {stats.best_percentage.toFixed(0)}%
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Best Score</p>
                </div>
              </div>

              {/* Trend Indicator */}
              {trend && (
                <div
                  className={`p-4 rounded-lg ${
                    trend.direction === "up"
                      ? "bg-green-500/10 border border-green-500/30"
                      : trend.direction === "down"
                      ? "bg-red-500/10 border border-red-500/30"
                      : "bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]"
                  }`}
                  data-testid="performance-trend"
                >
                  <div className="flex items-center gap-2">
                    {trend.direction === "up" ? (
                      <TrendingUp className="text-green-500" size={20} />
                    ) : trend.direction === "down" ? (
                      <TrendingDown className="text-red-500" size={20} />
                    ) : (
                      <Minus className="text-[hsl(var(--muted-foreground))]" size={20} />
                    )}
                    <span className="font-medium">
                      {trend.direction === "up"
                        ? "Improving!"
                        : trend.direction === "down"
                        ? "Needs Practice"
                        : "Steady Performance"}
                    </span>
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    {trend.message}
                  </p>
                </div>
              )}

              {/* Assigned Tests */}
              {assignedTests.length > 0 && (
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Play size={18} className="text-[hsl(var(--primary))]" />
                      Assigned Tests
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="assigned-tests-list">
                    {assignedTests.map((item) => {
                      const t = item.test;
                      const isExpanded = expandedTest === t.id;
                      const lb = testLeaderboards[t.id];

                      return (
                        <div key={t.id} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden" data-testid={`assigned-test-${t.id}`}>
                          <div className="p-4 bg-[hsl(var(--card))]">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold">{t.name}</h3>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                                  {t.cocktail_count} cocktails &bull; {t.test_type.replace('_', ' ')} &bull; {item.attempts} attempt{item.attempts !== 1 ? 's' : ''}
                                </p>
                              </div>
                              {item.attempts > 0 && (
                                <div className="text-right">
                                  <p className={`text-lg font-bold ${getScoreColor(item.best_percentage)}`}>
                                    {item.best_percentage.toFixed(0)}%
                                  </p>
                                  <p className="text-[10px] text-[hsl(var(--muted-foreground))]">best</p>
                                </div>
                              )}
                            </div>

                            {/* Recent scores bar */}
                            {item.recent_results.length > 0 && (
                              <div className="flex gap-1 mt-3">
                                {[...item.recent_results].reverse().map((r, idx) => (
                                  <div key={r.id || r.test_id || `${r.percentage}-${idx}`} className="flex-1" title={`${r.percentage?.toFixed(0)}%`}>
                                    <div className="h-1.5 rounded-full bg-[hsl(var(--background))]">
                                      <div
                                        className={`h-full rounded-full ${getProgressColor(r.percentage || 0)}`}
                                        style={{ width: `${r.percentage || 0}%` }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Action row */}
                            <div className="flex items-center gap-2 mt-3">
                              <Link to={`/study/${t.id}`}>
                                <Button variant="ghost" size="sm" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                                  <BookOpen size={12} className="mr-1" /> Study
                                </Button>
                              </Link>
                              <Link to={`/flashspecs/study/${t.id}`}>
                                <Button variant="ghost" size="sm" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                                  <Layers size={12} className="mr-1" /> FlashSpecs
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleLeaderboard(t.id)}
                                className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                                data-testid={`toggle-lb-staff-${t.id}`}
                              >
                                <Trophy size={12} className="mr-1" />
                                Leaderboard
                                {isExpanded ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
                              </Button>
                            </div>
                          </div>

                          {/* Per-test Leaderboard */}
                          {isExpanded && (
                            <div className="p-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--secondary))]" data-testid={`leaderboard-staff-${t.id}`}>
                              {!lb ? (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">Loading...</p>
                              ) : lb.leaderboard.length === 0 ? (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">No scores yet.</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {lb.leaderboard.map((entry) => (
                                    <div
                                      key={entry.rank}
                                      className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
                                        entry.rank <= 3 ? 'bg-[hsl(var(--primary))]/5' : ''
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className={`w-5 text-center font-bold text-xs ${
                                          entry.rank === 1 ? 'text-[#C36A2F]' : entry.rank === 2 ? 'text-gray-400' : entry.rank === 3 ? 'text-orange-500' : 'text-[hsl(var(--muted-foreground))]'
                                        }`}>
                                          {entry.rank}
                                        </span>
                                        <span className="font-medium text-sm">{entry.staff_name}</span>
                                      </div>
                                      <div className="flex items-center gap-3 text-xs">
                                        <span className="text-[hsl(var(--muted-foreground))]">{entry.attempts}x</span>
                                        <span className={`font-bold ${getScoreColor(entry.avg_score)}`}>
                                          {entry.avg_score.toFixed(0)}%
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Question Type Breakdown */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target size={18} className="text-[hsl(var(--primary))]" />
                    Accuracy by Question Type
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-testid="type-breakdown">
                  {Object.entries(typeStats).map(([type, typeStat]) => {
                    if (typeStat.total === 0) return null;
                    const pct = (typeStat.correct / typeStat.total) * 100;
                    const label = type.charAt(0).toUpperCase() + type.slice(1);
                    return (
                      <div key={type} data-testid={`type-stat-${type}`}>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-2">
                            {pct >= 80 ? <CheckCircle2 size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />}
                            <span className="text-sm font-medium">{label}</span>
                          </div>
                          <span className={`text-sm font-bold ${getScoreColor(pct)}`}>
                            {pct.toFixed(0)}% ({typeStat.correct}/{typeStat.total})
                          </span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-[hsl(var(--background))]">
                          <div className={`h-full rounded-full transition-all ${getProgressColor(pct)}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  {Object.values(typeStats).every(s => s.total === 0) && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
                      No test data to analyze yet
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Study Decks */}
              {studyDecks.length > 0 && (
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen size={18} className="text-[hsl(var(--primary))]" />
                      Study Decks
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2" data-testid="staff-study-decks">
                    {studyDecks.map((deck) => (
                      <Link key={deck.test_id} to={`/study/${deck.test_id}`}>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--card))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer">
                          <div className="flex items-center gap-2">
                            <BookOpen size={16} className="text-[hsl(var(--primary))]" />
                            <span className="text-sm font-medium">{deck.test_name}</span>
                          </div>
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">Study</span>
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Recent Test Results */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart2 size={18} className="text-[hsl(var(--primary))]" />
                    Recent Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2" data-testid="recent-tests-list">
                  {recentResults.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
                      No test history yet
                    </p>
                  ) : (
                    recentResults.map((result, idx) => (
                      <div
                        key={result.id || `${result.test_id}-${idx}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--card))] hover:bg-[hsl(var(--accent))] cursor-pointer transition-colors"
                        onClick={() => result.id && setReviewResultId(result.id)}
                        data-testid={`recent-test-${idx}`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">{result.test_name || "Test"}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">
                              {new Date(result.completed_at).toLocaleDateString()}
                            </p>
                            {result.correct_answers > 0 && (
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                {result.correct_answers} correct
                              </p>
                            )}
                            {result.perfect_cocktails > 0 && (
                              <p className="text-xs text-[hsl(var(--primary))]">
                                <Award size={12} className="inline mr-1" />
                                {result.perfect_cocktails} perfect
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xl font-bold ${getScoreColor(result.percentage)}`}>
                            {result.percentage?.toFixed(0)}%
                          </span>
                          <Eye size={16} className="text-[hsl(var(--muted-foreground))]" />
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Score Progress Chart */}
              {recentResults.length > 1 && (
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp size={18} />
                      Score History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-32 flex items-end gap-1">
                      {[...recentResults].reverse().map((result, idx) => {
                        const pct = result.percentage || 0;
                        return (
                          <div key={result.id || `${result.test_id}-${idx}`} className="flex-1 flex flex-col items-center justify-end">
                            <span className="text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                              {pct.toFixed(0)}%
                            </span>
                            <div
                              className={`w-full rounded-t ${getProgressColor(pct)}`}
                              style={{ height: `${Math.max(pct, 5)}%` }}
                              title={`${pct.toFixed(0)}% - ${result.test_name || "Test"}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                      <span>Oldest</span>
                      <span>Latest</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Setup Link Dialog */}
      <Dialog open={!!setupLink} onOpenChange={() => setSetupLink(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="setup-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="text-[hsl(var(--primary))]" size={20} />
              Account Setup Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Share this link with the user so they can set their password:
            </p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={setupLink || ""}
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-xs"
                data-testid="setup-link-input"
              />
              <Button onClick={copyLink} size="icon" className="shrink-0" data-testid="copy-link-btn">
                <Copy size={16} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Test Review Dialog */}
      <TestReviewDialog
        resultId={reviewResultId}
        open={!!reviewResultId}
        onClose={() => setReviewResultId(null)}
      />
    </div>
  );
}
